import dayjs from 'dayjs'
import { getAllWeldsOnComponent } from 'features/assetModeling'
import {
  calculateDerivedValues,
  deserializeTminSource,
  getRcaBinValueFunction,
} from 'features/dataLayers/generation/calculations/calculateDerivedValues'
import { hashStringFNV1a } from 'features/dataLayers/generation/generatedCdfSignature'
import {
  createGeckoAssetModel,
  GeckoComponentModelType,
  GeckoMeasurement,
  GeckoMeasurementUnit,
} from 'types'

import {
  calculatePopulationStandardDeviation,
  clamp,
  clampCellBoundsToGrid,
  collectShiftedValuePairs,
  createFilled2D,
  getElapsedYears,
  getFeatureRectangleCells,
  getGridValue,
  getMillimetersMultiplier,
  validate2DArrayShape,
} from './algorithmShared'
import {
  PRIOR_SUPPORT_MIN_VALUE_COUNT,
  SHORT_TERM_FEATURE_MIN_CELL_COUNT,
  TARGET_FEATURE_CORE_DFN_PERCENT,
  TARGET_FEATURE_GROW_DFN_PERCENT,
  TARGET_FEATURE_MERGE_DISTANCE_FEET,
  TARGET_FEATURE_MIN_AREA_FEET_SQUARED,
} from './constants'
import { extractTargetShortTermCorrosionFeatures } from './featureExtraction'
import { findPriorSupportForTargetFeature } from './priorMatching'
import type {
  CalculateShortTermFeatureRectangleRatesProps,
  CalculateShortTermFeatureRectangleRatesResult,
  CalculateShortTermFeatureRemainingLifeFromRcaValuesProps,
  CalculateShortTermFeatureRemainingLifeProps,
  CalculateShortTermFeatureRemainingLifeResult,
  ShortTermCorrosionFeatureRectangle,
  ShortTermCorrosionPriorSupport,
  ShortTermFeatureRateComputationProps,
  ShortTermFeatureRateComputationResult,
  ShortTermGridValues,
  ShortTermRateFeatureGridMetadata,
  ShortTermRatePriorSupport,
  ShortTermRatePriorSupportDiagnostic,
} from './types'

export function calculateShortTermFeatureRate(
  props: ShortTermFeatureRateComputationProps
): ShortTermFeatureRateComputationResult {
  validate2DArrayShape(props.targetThicknessValues, props.targetGrid)
  validate2DArrayShape(
    props.targetDifferenceFromNominalValues,
    props.targetGrid
  )
  validate2DArrayShape(props.comparisonThicknessValues, props.comparisonGrid)

  const elapsedYears = getElapsedYears(
    props.comparisonInspectionDate,
    props.targetInspectionDate
  )
  if (elapsedYears <= 0) {
    throw new Error(
      'Short-term corrosion rate requires the target inspection to be later than the comparison inspection.'
    )
  }

  const minFeatureCellCount =
    props.minFeatureCellCount ?? SHORT_TERM_FEATURE_MIN_CELL_COUNT
  const targetFeatures = extractTargetShortTermCorrosionFeatures({
    differenceFromNominalValues: props.targetDifferenceFromNominalValues,
    thicknessValues: props.targetThicknessValues,
    grid: props.targetGrid,
    coreDfnPercent:
      props.targetFeatureCoreDfnPercent ?? TARGET_FEATURE_CORE_DFN_PERCENT,
    growDfnPercent:
      props.targetFeatureGrowDfnPercent ?? TARGET_FEATURE_GROW_DFN_PERCENT,
    minFeatureCellCount,
    minFeatureAreaFeetSquared:
      props.minFeatureAreaFeetSquared ?? TARGET_FEATURE_MIN_AREA_FEET_SQUARED,
    mergeDistanceFeet:
      props.targetFeatureMergeDistanceFeet ??
      TARGET_FEATURE_MERGE_DISTANCE_FEET,
  })
  const rateMmPerYear = createFilled2D(
    props.targetGrid.yBinCount,
    props.targetGrid.xBinCount,
    Number.NaN
  )
  const millimetersMultiplier = getMillimetersMultiplier(
    props.measurementUnitId
  )
  const priorSupports: ShortTermRatePriorSupportDiagnostic[] = []
  let supportedFeatureCount = 0

  for (const targetFeature of targetFeatures) {
    const priorSupport = findPriorSupportForTargetFeature({
      targetFeature,
      targetThicknessValues: props.targetThicknessValues,
      comparisonThicknessValues: props.comparisonThicknessValues,
      targetGrid: props.targetGrid,
      comparisonGrid: props.comparisonGrid,
      options: {
        rectPaddingFeet: props.targetFeatureRectPaddingFeet,
        searchRadiusFeet: props.priorSupportSearchRadiusFeet,
        translationStepBins: props.priorSupportTranslationStepBins,
        distanceWeight: props.priorSupportDistanceWeight,
        minCoverageFraction: props.priorSupportMinCoverageFraction,
        missingCoverageWeight: props.priorSupportMissingCoverageWeight,
        minValueCount: props.priorSupportMinValueCount,
      },
    })
    if (!priorSupport) continue

    const featureRate = calculateFeatureRateMmPerYear({
      targetThicknessValues: targetFeature.thicknessValues,
      comparisonThicknessValues: priorSupport.comparisonThicknessValues,
      millimetersMultiplier,
      elapsedYears,
    })
    if (!Number.isFinite(featureRate)) continue

    supportedFeatureCount += 1
    priorSupports.push(stripPriorSupportValues(priorSupport))

    for (const cell of targetFeature.cells) {
      rateMmPerYear[cell.y][cell.x] = featureRate
    }
  }

  const includeFeatureDetails = props.includeFeatureDetails ?? true

  return {
    rateMmPerYear,
    targetFeatures: includeFeatureDetails ? targetFeatures : [],
    priorSupports: includeFeatureDetails ? priorSupports : [],
    summary: {
      elapsedYears,
      targetFeatureCount: targetFeatures.length,
      supportedFeatureCount,
      unsupportedFeatureCount: targetFeatures.length - supportedFeatureCount,
    },
  }
}

export function calculateShortTermCorrosionRectangleRates({
  featureRectangles,
  priorSupports,
  targetThicknessValues,
  comparisonThicknessValues,
  targetGrid,
  comparisonGrid,
  measurementUnitId,
  targetInspectionDate,
  comparisonInspectionDate,
  priorSupportMinValueCount = PRIOR_SUPPORT_MIN_VALUE_COUNT,
  includeFeatureDetails = true,
}: CalculateShortTermFeatureRectangleRatesProps): CalculateShortTermFeatureRectangleRatesResult {
  validate2DArrayShape(targetThicknessValues, targetGrid)
  validate2DArrayShape(comparisonThicknessValues, comparisonGrid)

  const elapsedYears = getElapsedYears(
    comparisonInspectionDate,
    targetInspectionDate
  )
  if (elapsedYears <= 0) {
    throw new Error(
      'Short-term corrosion rate requires the target inspection to be later than the comparison inspection.'
    )
  }

  const rateMmPerYear = createFilled2D(
    targetGrid.yBinCount,
    targetGrid.xBinCount,
    Number.NaN
  )
  const millimetersMultiplier = getMillimetersMultiplier(measurementUnitId)
  const supportByFeatureId = new Map(
    priorSupports.map((support) => [support.featureId, support])
  )
  const usedSupports: ShortTermCorrosionPriorSupport[] = []
  let supportedFeatureCount = 0

  for (const featureRectangle of featureRectangles) {
    const support = supportByFeatureId.get(featureRectangle.id)
    if (!support) continue

    const rectangleCells = getFeatureRectangleCells({
      targetCellBounds: featureRectangle.targetCellBounds,
      targetGrid,
    })
    const pairs = collectShiftedValuePairs({
      cells: rectangleCells,
      targetValues: targetThicknessValues,
      comparisonValues: comparisonThicknessValues,
      targetGrid,
      comparisonGrid,
      shiftXFeet: support.shiftXFeet,
      shiftYFeet: support.shiftYFeet,
    })

    if (pairs.pairs.length < priorSupportMinValueCount) continue

    const featureRate = calculateFeatureRateMmPerYear({
      targetThicknessValues: pairs.pairs.map((pair) => pair.targetValue),
      comparisonThicknessValues: pairs.pairs.map(
        (pair) => pair.comparisonValue
      ),
      millimetersMultiplier,
      elapsedYears,
    })
    if (!Number.isFinite(featureRate)) continue

    supportedFeatureCount += 1
    usedSupports.push(support)
    paintFeatureRectangle({
      rateMmPerYear,
      featureRectangle,
      targetGrid,
      value: featureRate,
    })
  }

  return {
    rateMmPerYear,
    featureRectangles: includeFeatureDetails ? [...featureRectangles] : [],
    priorSupports: includeFeatureDetails ? usedSupports : [],
    summary: {
      elapsedYears,
      targetFeatureCount: featureRectangles.length,
      supportedFeatureCount,
      unsupportedFeatureCount: featureRectangles.length - supportedFeatureCount,
    },
  }
}

export function calculateShortTermFeatureRemainingLife({
  componentId,
  assetModelJson,
  forecastComponentConfig,
  tminSource: serializedTminSource,
  targetThicknessValues,
  shortTermRateValuesMmPerYear,
  targetGrid,
  measurementUnitId,
  targetInspectionDate,
}: CalculateShortTermFeatureRemainingLifeProps): CalculateShortTermFeatureRemainingLifeResult {
  validate2DArrayShape(targetThicknessValues, targetGrid)
  validate2DArrayShape(shortTermRateValuesMmPerYear, targetGrid)

  const assetModel = createGeckoAssetModel(assetModelJson)
  const component = assetModel.data.components.get(componentId)
  if (!component) {
    throw new Error(`Component with id ${componentId} not found in asset model`)
  }

  const binWidth = GeckoMeasurement.CreateFromValue(
    targetGrid.binWidthFeet,
    GeckoMeasurementUnit.Feet
  )
  const binHeight = GeckoMeasurement.CreateFromValue(
    targetGrid.binHeightFeet,
    GeckoMeasurementUnit.Feet
  )
  const yGeometryStart = GeckoMeasurement.CreateFromValue(
    targetGrid.yStartFeet,
    GeckoMeasurementUnit.Feet
  )
  const xGeometryStart = GeckoMeasurement.CreateFromValue(
    targetGrid.xStartFeet,
    GeckoMeasurementUnit.Feet
  )
  const thicknessUnit = GeckoMeasurementUnit.GetOrCreate(measurementUnitId)
  const calculateRcaForBin = getRcaBinValueFunction({
    binWidth,
    binHeight,
    tminSource: deserializeTminSource(serializedTminSource),
    weldLines: getAllWeldsOnComponent(component),
    shellCourses:
      component.type === GeckoComponentModelType.VerticalTankShell
        ? component.coursesArray
        : [],
    forecastComponentConfig,
    inspectionDate: dayjs(targetInspectionDate),
    assetModel,
    componentId,
    componentType: component.type,
    repairsToSimInstall: [],
  })
  const rcaMmValues = calculateDerivedValues(
    componentId,
    toDerivedValuesGrid(targetThicknessValues, targetGrid),
    thicknessUnit,
    binHeight,
    binWidth,
    yGeometryStart,
    xGeometryStart,
    calculateRcaForBin
  )

  return calculateShortTermFeatureRemainingLifeFromRcaValues({
    rcaMmValues,
    shortTermRateValuesMmPerYear,
    targetGrid,
  })
}

export function calculateShortTermFeatureRemainingLifeFromRcaValues({
  rcaMmValues,
  shortTermRateValuesMmPerYear,
  targetGrid,
}: CalculateShortTermFeatureRemainingLifeFromRcaValuesProps): CalculateShortTermFeatureRemainingLifeResult {
  validate2DArrayShape(rcaMmValues, targetGrid)
  validate2DArrayShape(shortTermRateValuesMmPerYear, targetGrid)

  const remainingLifeYears = createFilled2D(
    targetGrid.yBinCount,
    targetGrid.xBinCount,
    Number.NaN
  )
  let calculatedBinCount = 0
  let blankBinCount = 0

  for (let y = 0; y < targetGrid.yBinCount; y += 1) {
    for (let x = 0; x < targetGrid.xBinCount; x += 1) {
      const rcaMm = getGridValue(rcaMmValues, targetGrid, x, y)
      const shortTermRateMmPerYear = getGridValue(
        shortTermRateValuesMmPerYear,
        targetGrid,
        x,
        y
      )
      if (!Number.isFinite(rcaMm) || !Number.isFinite(shortTermRateMmPerYear)) {
        blankBinCount += 1
        continue
      }

      const remainingLife = calculateRemainingLifeYearsFromRcaAndRate({
        rcaMm,
        shortTermRateMmPerYear,
      })
      if (Number.isNaN(remainingLife)) {
        blankBinCount += 1
        continue
      }

      remainingLifeYears[y][x] = remainingLife
      calculatedBinCount += 1
    }
  }

  return {
    remainingLifeYears,
    summary: {
      calculatedBinCount,
      blankBinCount,
    },
  }
}

export function calculateRemainingLifeYearsFromRcaAndRate({
  rcaMm,
  shortTermRateMmPerYear,
}: {
  rcaMm: number
  shortTermRateMmPerYear: number
}): number {
  const positiveShortTermRate = Math.max(0, shortTermRateMmPerYear)
  const remainingLifeYears = rcaMm / positiveShortTermRate
  return clamp(remainingLifeYears, -10, 150)
}

function toDerivedValuesGrid(
  values: ShortTermGridValues,
  grid: ShortTermRateFeatureGridMetadata
): ReadonlyArray<ReadonlyArray<number>> {
  if (!(values instanceof Float32Array)) {
    return values
  }

  return Array.from({ length: grid.yBinCount }, (_, y) =>
    Array.from({ length: grid.xBinCount }, (_, x) =>
      getGridValue(values, grid, x, y)
    )
  )
}

export function calculateFeatureRateMmPerYear({
  targetThicknessValues,
  comparisonThicknessValues,
  millimetersMultiplier,
  elapsedYears,
}: {
  targetThicknessValues: readonly number[]
  comparisonThicknessValues: readonly number[]
  millimetersMultiplier: number
  elapsedYears: number
}): number {
  if (elapsedYears <= 0 || millimetersMultiplier <= 0) return Number.NaN

  const targetValues = targetThicknessValues
    .filter(Number.isFinite)
    .map((value) => value * millimetersMultiplier)
    .toSorted((left, right) => left - right)
  const comparisonValues = comparisonThicknessValues
    .filter(Number.isFinite)
    .map((value) => value * millimetersMultiplier)
    .toSorted((left, right) => left - right)
  const sampleCount = Math.min(targetValues.length, comparisonValues.length)

  if (sampleCount === 0) return Number.NaN

  const deltas = Array.from({ length: sampleCount }, (_, index) => {
    const comparisonValue = sampleSortedValuesByRank(
      comparisonValues,
      index,
      sampleCount
    )
    const targetValue = sampleSortedValuesByRank(
      targetValues,
      index,
      sampleCount
    )
    return Math.max(0, comparisonValue - targetValue)
  })
  const mean = deltas.reduce((sum, value) => sum + value, 0) / deltas.length
  const standardDeviation = calculatePopulationStandardDeviation(deltas, mean)

  return (mean + 2 * standardDeviation) / elapsedYears
}

export function getShortTermFeatureRectanglesHash(
  featureRectangles: readonly ShortTermCorrosionFeatureRectangle[]
): string {
  const signature = featureRectangles
    .toSorted((left, right) => left.id.localeCompare(right.id))
    .map((rectangle) =>
      [
        rectangle.id,
        rectangle.source,
        rectangle.targetCellBounds.minX,
        rectangle.targetCellBounds.maxX,
        rectangle.targetCellBounds.minY,
        rectangle.targetCellBounds.maxY,
      ].join(':')
    )
    .join('|')

  return hashStringFNV1a(signature, 0x811c9dc5)
}

function paintFeatureRectangle({
  rateMmPerYear,
  featureRectangle,
  targetGrid,
  value,
}: {
  rateMmPerYear: number[][]
  featureRectangle: ShortTermCorrosionFeatureRectangle
  targetGrid: ShortTermRateFeatureGridMetadata
  value: number
}) {
  const cellBounds = clampCellBoundsToGrid(
    featureRectangle.targetCellBounds,
    targetGrid
  )

  for (let y = cellBounds.minY; y <= cellBounds.maxY; y += 1) {
    for (let x = cellBounds.minX; x <= cellBounds.maxX; x += 1) {
      rateMmPerYear[y][x] = value
    }
  }
}

function stripPriorSupportValues(
  support: ShortTermRatePriorSupport
): ShortTermRatePriorSupportDiagnostic {
  return {
    targetFeatureId: support.targetFeatureId,
    shiftXFeet: support.shiftXFeet,
    shiftYFeet: support.shiftYFeet,
    searchRadiusFeet: support.searchRadiusFeet,
    score: support.score,
    coverageFraction: support.coverageFraction,
    patternMismatch: support.patternMismatch,
    normalizedDistance: support.normalizedDistance,
  }
}

function sampleSortedValuesByRank(
  sortedValues: readonly number[],
  sampleIndex: number,
  sampleCount: number
) {
  const valueIndex = clamp(
    Math.floor(((sampleIndex + 0.5) * sortedValues.length) / sampleCount),
    0,
    sortedValues.length - 1
  )
  return sortedValues[valueIndex]
}
